import React, { useState } from 'react';
import { CAPTURE_STROKE_PX } from '../../utils/elevationPenWeights';
import { HIGHLIGHT_COLORS } from '../../constants/highlightColors';
import { stopCanvasPan } from '../../utils/elevationMeasurementUtils';
import { WINDOW_BAND_Y_BUFFER_M } from '@shared-lib/kitchen-assembly/styling/kitchenBacksplash';
import { dragDeltaM } from '../../utils/elevationBandDrag';
import {
  arrowPath,
  GRAB_HANDLE_RADIUS_PX,
  GRAB_HANDLE_HIT_RADIUS_PX,
} from '../../utils/elevationGrabHandle';
import { handleSizing } from '../../utils/handleSizing';
import {
  topMeetsOpening,
  sideMeetsOpening,
  BAND_SIDE_HINTS,
  GLASS_SIDE_HINT,
  EDGE_PX,
  SECTION_BAND,
} from '../../utils/elevationBands';

/**
 * The finishes laid ON a wall face — backsplash, shower or tub tile, tile and
 * paint sections (item #1) — drawn as the elevation's lowest layer: over the
 * wall rectangle and UNDER the end-on bands and every object, so a cabinet
 * standing in front stays clickable.
 *
 * Call item #28: a band that is one of the wall's Backsplash-card bands
 * (`band.band`, from `wallFinishSpans`) can be hovered and picked when the
 * host hands in an `interaction`, and its top edge dragged to set its height;
 * so can a tile section — a wainscot, tile drawn in Edit wall — as the
 * `SECTION_BAND` (PR 5), outlined by region. Everything else — paint, a band
 * on a wall with no card, every band on a preview tile or a capture — never
 * takes the pointer, exactly as before. The PICKED band's grip, handle, side hints, snap lines
 * and readout are `BandGripLayer`, drawn above the objects.
 */

// Tile is a fine diagonal hatch on a CLEAR ground, the wall showing through —
// no wash, because shading on an elevation means something you cannot edit
// (a side-on object, an opening), and a backsplash is neither. A painted
// section is a wash of its own colour: that is the finish itself, not a
// shade. Both edged in a warm grey a shade lighter than an object's stroke,
// so the band's extent reads.
const WALL_FINISH_HATCH = 'rgba(120, 102, 80, 0.30)';
const WALL_FINISH_STROKE = 'rgba(96, 82, 64, 0.55)';
const WALL_FINISH_LABEL = '#5c5246';

// A band under the pointer, and the band that is picked — the selection's
// own pink (`HIGHLIGHT_COLORS.PRIMARY`), a lighter wash for a hover.
const BAND_HIGHLIGHT = {
  hover: { fill: 'rgba(255, 102, 255, 0.10)', strokePx: 1 },
  selected: { fill: 'rgba(255, 102, 255, 0.15)', strokePx: 1.5 },
};
// The run handles' blue, and the accent the snap lines are drawn in.
const HANDLE_BLUE = '#0066ff';
const SNAP_ACCENT = '#DD9B4A';
// A top-edge grip is the plan's handle target tall (`handleSizing` — the one
// size table), centred on the edge: 52 px on a finger, its 18 px mouse target
// with a mouse. The handle disc is the elevation's run / bay handle
// (`GRAB_HANDLE_*`), its arrow up and down (Yu-Han, 2026-10-22).
const gripHeightPx = (coarse) => handleSizing(coarse).hitPx;
const SIDE_PX = { fine: 8, coarse: 14 };
const INCH_M = 0.0254;
const TOP_HINT = 'Drag to change the height';
// Travel below this is a press, not a drag — a tap or a trackpad click must
// not move a setting.
const DEAD_ZONE_PX = 3;
// How long after a drag the click it ends with is still expected.
const DRAG_CLICK_MS = 250;

/**
 * How far from a window's sill a piece's top still counts as cut at the
 * glass: the band builder stops a piece 1 cm under it
 * (`WINDOW_BAND_Y_BUFFER_M`), at this drawing's scale.
 */
const glassTolPx = (scale) => EDGE_PX + WINDOW_BAND_Y_BUFFER_M * scale;

/** Can a drag move this band yet (`interaction.dragBands`)? */
const draggableBand = (interaction, band) =>
  !!interaction?.onDragStart && (interaction.dragBands || []).includes(band);

const hexToRgba = (hex, alpha) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

/** The band's fill: a paint's own colour, else the tile hatch alone. */
export const wallFinishBackground = (band) => {
  const paint = band.color ? hexToRgba(band.color, 0.32) : null;
  if (paint) return { backgroundColor: paint };
  return {
    backgroundImage: `repeating-linear-gradient(45deg, ${WALL_FINISH_HATCH} 0 1px, transparent 1px 7px)`,
  };
};

/**
 * Does `highlight` ({band, wallIds, regionIds?}) name this drawn band? A tile
 * section's names its regions — the wall's other sections are not it.
 */
const names = (highlight, band) =>
  !!highlight &&
  !!band.band &&
  highlight.band === band.band &&
  (highlight.wallIds || []).includes(band.wallId) &&
  (!highlight.regionIds || highlight.regionIds.includes(band.regionId));

/**
 * Is this piece's top an opening's edge rather than the finish's own top —
 * nothing to drag there? A piece the drawing cut under a door or window
 * (`topCut`), and a card band the band builder stops under the glass. A tile
 * section's own top is wherever it was drawn, sill or not.
 */
const cutAtGlass = (band, openingRects, tolPx) =>
  !!band.topCut ||
  (band.band !== SECTION_BAND && topMeetsOpening(band, openingRects, tolPx));

/** What a pick, a hover or a drag reports: the band as the drawing names it. */
const identOf = (band) => ({
  wallId: band.wallId,
  band: band.band,
  regionId: band.regionId,
});

/** Where a world height sits on a band's wall, in px from the top. */
const pxOfWorld = (band, worldY, { maxHeight, scale }) =>
  band.frame ? (maxHeight - band.frame.toDrawing(worldY)) * scale : null;

/**
 * Start dragging a band's top edge from a press on its grip or handle.
 *
 * The press stops the canvas pan (`stopCanvasPan` — the viewport pans on
 * POINTER events, reference-docs/elevations "Traps"). The move / release
 * listeners go on the WINDOW, not the grip: the preview redraws the band —
 * new pieces, new elements — under the pointer, and a capture held by a
 * removed element is lost mid-drag. Travel is handed up in metres,
 * `pxPerMetre` carrying the drawing's scale AND the view's zoom.
 */
function startBandDrag(e, band, interaction, pxPerMetre) {
  if (!band.frame) return;
  stopCanvasPan(e);
  e.preventDefault?.();
  const pointerId = e.pointerId;
  const startClientY = e.clientY;
  let travelled = false;
  const mine = (ev) =>
    pointerId == null || ev.pointerId == null || ev.pointerId === pointerId;
  const stop = () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onCancel);
  };
  const onMove = (ev) => {
    if (!mine(ev)) return;
    const dy = ev.clientY - startClientY;
    if (!travelled && Math.abs(dy) < DEAD_ZONE_PX) return;
    travelled = true;
    interaction.onDragMove?.(dragDeltaM(dy, pxPerMetre), {
      shiftKey: !!ev.shiftKey,
    });
  };
  const onUp = (ev) => {
    if (!mine(ev)) return;
    stop();
    if (travelled) swallowNextClick();
    interaction.onDragEnd?.();
  };
  const onCancel = (ev) => {
    if (!mine(ev)) return;
    stop();
    interaction.onDragCancel?.();
  };
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onCancel);
  interaction.onDragStart?.(identOf(band), band.frame.toWorld(band.topM));
}

/** A grip never counts as a click on the wall (which would deselect). */
const swallowClick = (e) => e.stopPropagation();

/**
 * The click a drag ends with lands wherever the pointer was let go — on the
 * wall, or past the drawing — and would read as a click on empty wall, which
 * closes the band's card and its Undo. Caught once, before anything sees it.
 */
function swallowNextClick() {
  const swallow = (ev) => {
    ev.stopPropagation();
    ev.preventDefault();
  };
  window.addEventListener('click', swallow, { capture: true, once: true });
  setTimeout(
    () => window.removeEventListener('click', swallow, { capture: true }),
    DRAG_CLICK_MS
  );
}

/**
 * @param {object} props
 * @param {Array<object>} props.bands the placed finishes — `wallFinishSpans`
 *   plus their px box (`left`/`top`/`width`/`height`), `showLabel`, `topM`
 *   (the top in drawing metres) and `frame` (`bandHeightFrame`)
 * @param {boolean} props.isCapture
 * @param {number} props.finishLabelPx
 * @param {?object} props.interaction null on every host that does not edit
 *   bands: `{selected, hovered, onHover, onSelect, onDragStart, …}`
 * @param {(e) => boolean} props.isClickNotDrag the drawing's own click test,
 *   so a press that panned the view picks nothing
 * @param {boolean} [props.isPanning]
 * @param {Array<object>} [props.openingRects] the windows' px boxes
 * @param {number} [props.pxPerMetre] scale × zoom
 * @param {boolean} [props.coarsePointer]
 * @param {number} [props.zoom] the view's zoom, which grip sizes are kept
 *   on screen against
 */
export default function WallFinishBandLayer({
  bands,
  isCapture,
  finishLabelPx,
  interaction = null,
  isClickNotDrag = () => true,
  isPanning = false,
  openingRects = [],
  pxPerMetre = 1,
  coarsePointer = false,
  zoom = 1,
  // the drawing's px per metre
  scale = 1,
}) {
  const gripPx = gripHeightPx(coarsePointer) / (zoom || 1);
  const tolPx = glassTolPx(scale);
  return bands.map((band) => {
    const pickable = !!interaction && !!band.band;
    const picked = pickable && names(interaction.selected, band);
    const highlight = !pickable
      ? null
      : picked
        ? 'selected'
        : names(interaction.hovered, band)
          ? 'hover'
          : null;
    // An unpicked band's top edge can be grabbed straight away — under the
    // objects, so only where nothing stands in front of it. The PICKED
    // band's grip is `BandGripLayer`'s, over the objects.
    const topGrip =
      pickable &&
      !picked &&
      draggableBand(interaction, band.band) &&
      !cutAtGlass(band, openingRects, tolPx);
    return (
      <React.Fragment key={`wall-finish-${band.key}`}>
        <div
          data-elevation-wall-finish={band.kind}
          {...(pickable ? { 'data-elevation-band': band.band } : {})}
          title={band.label}
          style={{
            position: 'absolute',
            left: band.left,
            top: band.top,
            width: band.width,
            height: band.height,
            ...wallFinishBackground(band),
            border: `${isCapture ? CAPTURE_STROKE_PX.object : 0.75}px solid ${WALL_FINISH_STROKE}`,
            boxSizing: 'border-box',
            overflow: 'hidden',
            pointerEvents: pickable ? 'auto' : 'none',
            cursor: pickable ? 'pointer' : undefined,
          }}
          // A press on the band body still pans the view (no pan guard
          // here): only a press that did not travel picks it.
          onClick={
            pickable
              ? (e) => {
                  if (isPanning || !isClickNotDrag(e)) return;
                  e.stopPropagation();
                  interaction.onSelect(identOf(band));
                }
              : undefined
          }
          onMouseEnter={
            pickable ? () => interaction.onHover?.(identOf(band)) : undefined
          }
          onMouseLeave={
            pickable ? () => interaction.onHover?.(null) : undefined
          }
        >
          {band.showLabel ? (
            <span
              style={{
                position: 'absolute',
                left: finishLabelPx * 0.4,
                top: finishLabelPx * 0.2,
                fontSize: finishLabelPx,
                lineHeight: 1.2,
                fontWeight: 500,
                letterSpacing: 0.2,
                color: WALL_FINISH_LABEL,
                whiteSpace: 'nowrap',
              }}
            >
              {band.label}
            </span>
          ) : null}
          {highlight ? (
            <div
              data-elevation-band-highlight={highlight}
              style={{
                position: 'absolute',
                inset: 0,
                background: BAND_HIGHLIGHT[highlight].fill,
                border: `${BAND_HIGHLIGHT[highlight].strokePx}px solid ${HIGHLIGHT_COLORS.PRIMARY}`,
                boxSizing: 'border-box',
                pointerEvents: 'none',
              }}
            />
          ) : null}
        </div>
        {topGrip ? (
          <div
            data-band-grip="top"
            title={TOP_HINT}
            onPointerDown={(e) =>
              startBandDrag(e, band, interaction, pxPerMetre)
            }
            onClick={swallowClick}
            style={{
              position: 'absolute',
              left: band.left,
              top: band.top - gripPx / 2,
              width: band.width,
              height: gripPx,
              cursor: 'ns-resize',
              touchAction: 'none',
            }}
          />
        ) : null}
      </React.Fragment>
    );
  });
}

/** The pieces of the picked band, and the one its handle sits on. */
function pickedPieces(bands, interaction, openingRects, tolPx) {
  const selected = interaction?.selected;
  const pieces = bands.filter((b) => names(selected, b));
  const ownWall = selected?.wallIds?.[0];
  const draggable = pieces.filter((b) => !cutAtGlass(b, openingRects, tolPx));
  const onOwnWall = draggable.filter((b) => b.wallId === ownWall);
  // the TALLEST — the piece the setting reaches, not one an upper stops
  // short of it — the widest of those
  const handlePiece = (onOwnWall.length ? onOwnWall : draggable).reduce(
    (best, b) =>
      !best ||
      b.top < best.top - EDGE_PX ||
      (Math.abs(b.top - best.top) <= EDGE_PX && b.width > best.width)
        ? b
        : best,
    null
  );
  return { pieces, draggable, handlePiece };
}

/** Each wall's stretch of the picked band: its left and right in px. */
function extentsByWall(pieces) {
  const out = new Map();
  for (const b of pieces) {
    const e = out.get(b.wallId);
    if (!e)
      out.set(b.wallId, { left: b.left, right: b.left + b.width, band: b });
    else {
      e.left = Math.min(e.left, b.left);
      e.right = Math.max(e.right, b.left + b.width);
    }
  }
  return out;
}

/**
 * The PICKED band's own controls, drawn ABOVE the objects (call item #28):
 * the top-edge grip of every piece whose top a setting moves, a handle, the
 * side edges (which do not drag in v1 and say why), and — while dragging —
 * the snap lines across the band's walls, a dashed line at the height where
 * an upper stops the band below it, and a readout beside the handle.
 *
 * Over the objects so the grip is reachable where a counter band's top IS
 * an upper cabinet's bottom edge — the one place a cabinet's bottom few
 * pixels give way to the band. Sizes on screen (lines, text, the readout)
 * are divided by the zoom, so they stay one size as the drawing scales.
 */
export function BandGripLayer({
  bands,
  interaction,
  openingRects = [],
  pxPerMetre = 1,
  scale = 1,
  maxHeight = 0,
  zoom = 1,
  coarsePointer = false,
}) {
  const [hint, setHint] = useState(null);
  if (
    !interaction?.selected ||
    !draggableBand(interaction, interaction.selected.band)
  ) {
    return null;
  }
  const tolPx = glassTolPx(scale);
  const { pieces, draggable, handlePiece } = pickedPieces(
    bands,
    interaction,
    openingRects,
    tolPx
  );
  if (!pieces.length) return null;

  const z = zoom || 1;
  const fine = coarsePointer ? 'coarse' : 'fine';
  const gripPx = gripHeightPx(coarsePointer);
  const sidePx = SIDE_PX[fine];
  const handleR = GRAB_HANDLE_RADIUS_PX / z;
  const handleHit = GRAB_HANDLE_HIT_RADIUS_PX / z;
  const drag =
    interaction.drag && interaction.drag.band === interaction.selected.band
      ? interaction.drag
      : null;
  const at = { maxHeight, scale };
  const valuePx =
    drag && handlePiece ? pxOfWorld(handlePiece, drag.valueY, at) : null;
  const handleX = handlePiece ? handlePiece.left + handlePiece.width / 2 : null;
  const handleY = valuePx ?? handlePiece?.top ?? null;
  const halfInchPx = (INCH_M / 2) * scale;
  // An upper (or the window head) stops the band here below the height set.
  const stopsBelow =
    !!drag &&
    drag.band === 'counter' &&
    valuePx != null &&
    handlePiece.top > valuePx + halfInchPx;
  const sideHint = (b, side) =>
    b.band !== SECTION_BAND && sideMeetsOpening(b, side, openingRects, tolPx)
      ? GLASS_SIDE_HINT
      : BAND_SIDE_HINTS[b.band] || '';
  const showHint = (text, left, top) => setHint({ text, left, top });
  const extents = extentsByWall(pieces);
  const layer = { position: 'absolute', zIndex: 20 };

  return (
    <>
      {draggable.map((b) => (
        <div
          key={`grip-top-${b.key}`}
          data-band-grip="top"
          title={TOP_HINT}
          onPointerDown={(e) => startBandDrag(e, b, interaction, pxPerMetre)}
          onClick={swallowClick}
          style={{
            ...layer,
            left: b.left,
            top: b.top - gripPx / z / 2,
            width: b.width,
            height: gripPx / z,
            cursor: 'ns-resize',
            touchAction: 'none',
          }}
        />
      ))}
      {pieces.flatMap((b) =>
        ['left', 'right'].map((side) => {
          const text = sideHint(b, side);
          const x = side === 'left' ? b.left : b.left + b.width;
          const top = b.top + gripPx / z / 2;
          return (
            <div
              key={`grip-${side}-${b.key}`}
              data-band-grip="side"
              data-band-hint={text}
              title={text}
              onMouseEnter={() => showHint(text, x, top)}
              onMouseLeave={() => setHint(null)}
              onPointerDown={(e) => {
                stopCanvasPan(e);
                showHint(text, x, top);
              }}
              onClick={swallowClick}
              style={{
                ...layer,
                left: x - sidePx / z / 2,
                top,
                width: sidePx / z,
                height: Math.max(0, b.height - gripPx / z / 2),
                cursor: 'not-allowed',
              }}
            />
          );
        })
      )}
      {handlePiece && handleY != null ? (
        <svg
          data-band-grip="handle"
          width={handleHit * 2}
          height={handleHit * 2}
          onPointerDown={(e) =>
            startBandDrag(e, handlePiece, interaction, pxPerMetre)
          }
          onClick={swallowClick}
          style={{
            ...layer,
            left: handleX - handleHit,
            top: handleY - handleHit,
            overflow: 'visible',
            cursor: 'ns-resize',
            touchAction: 'none',
          }}
        >
          <circle
            cx={handleHit}
            cy={handleHit}
            r={handleR}
            fill={HANDLE_BLUE}
            stroke="#ffffff"
            strokeWidth={2 / z}
          />
          {/* The one axis a band's top edge travels: up and down. */}
          <path
            d={arrowPath(
              handleHit,
              handleHit,
              handleR * 0.55,
              handleR * 0.22,
              'vertical'
            )}
            stroke="#ffffff"
            strokeWidth={2 / z}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      ) : null}
      {drag
        ? drag.snaps.flatMap((snap) =>
            [...extents.entries()].map(([wallId, e]) => {
              // a line that names its wall is a height on that wall only
              if (snap.wallId && snap.wallId !== wallId) return null;
              const y = pxOfWorld(e.band, snap.y, at);
              if (y == null) return null;
              const matched = drag.snapChip === snap.chip;
              return (
                <div
                  key={`guide-${snap.chip}-${wallId}`}
                  data-band-guide={snap.chip}
                  data-matched={matched ? 'true' : 'false'}
                  style={{
                    ...layer,
                    left: e.left - 4 / z,
                    top: y,
                    width: e.right - e.left + 10 / z,
                    height: 0,
                    borderTop: `${(matched ? 2 : 1) / z}px ${matched ? 'solid' : 'dashed'} ${SNAP_ACCENT}`,
                    pointerEvents: 'none',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      left: '100%',
                      top: -7 / z,
                      marginLeft: 4 / z,
                      fontSize: 10.5 / z,
                      fontWeight: matched ? 700 : 400,
                      color: matched ? '#8A5A18' : '#B67332',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {matched ? '✓ ' : ''}
                    {snap.name}
                  </span>
                </div>
              );
            })
          )
        : null}
      {stopsBelow
        ? [...extents.values()].map((e) => (
            <div
              key={`value-${e.band.wallId}`}
              data-band-value-line
              style={{
                ...layer,
                left: e.left,
                top: pxOfWorld(e.band, drag.valueY, at),
                width: e.right - e.left,
                height: 0,
                borderTop: `${1 / z}px dashed ${HIGHLIGHT_COLORS.PRIMARY}`,
                pointerEvents: 'none',
              }}
            />
          ))
        : null}
      {drag?.readout && handlePiece ? (
        <div
          data-band-readout
          style={{
            ...layer,
            left: handlePiece.left + handlePiece.width + 14 / z,
            top: handleY,
            transform: `translateY(-50%) scale(${1 / z})`,
            transformOrigin: 'left center',
            background: '#25241f',
            color: '#F3E9D2',
            borderRadius: 8,
            borderLeft: `3px solid ${drag.snapChip ? SNAP_ACCENT : '#8a8a8a'}`,
            padding: '6px 10px',
            fontSize: 12,
            lineHeight: 1.35,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 14px rgba(0,0,0,.25)',
            pointerEvents: 'none',
          }}
        >
          <b
            style={{
              display: 'block',
              fontSize: 13,
              color: drag.snapChip ? SNAP_ACCENT : '#ffffff',
            }}
          >
            {drag.readout.title}
          </b>
          <span style={{ display: 'block' }}>{drag.readout.measure}</span>
          {stopsBelow ? (
            <span style={{ display: 'block', color: '#f2c48d' }}>
              Stops under the upper cabinet here
            </span>
          ) : null}
          {drag.readout.also?.length ? (
            <span style={{ display: 'block', fontWeight: 600 }}>
              Also changes: {drag.readout.also.join(', ')}
            </span>
          ) : null}
          {drag.readout.refusal ? (
            <span style={{ display: 'block', color: '#f2c48d' }}>
              {drag.readout.refusal}
            </span>
          ) : null}
        </div>
      ) : null}
      {hint ? (
        <div
          data-band-side-hint
          style={{
            ...layer,
            left: hint.left + 10 / z,
            top: hint.top,
            transform: `scale(${1 / z})`,
            transformOrigin: 'left top',
            background: '#ffffff',
            color: 'var(--color-util-text)',
            border: '1px solid var(--color-border)',
            borderRadius: 6,
            padding: '5px 8px',
            fontSize: 11.5,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 10px rgba(0,0,0,.12)',
            pointerEvents: 'none',
          }}
        >
          {hint.text}
        </div>
      ) : null}
    </>
  );
}
